/**
 * Logo — the NEXIRT brand component.
 *
 * API:
 *   <Logo variant="horizontal" size="md" colorMode="mono-light" />
 *   <Logo variant="mark-only" size="sm" concept="apex" />
 *   <Logo variant="stacked"   size="lg" concept="refraction" colorMode="primary" />
 *
 * Defaults: concept=apex, variant=horizontal, colorMode=mono-light, size=md.
 *
 * Per BRAND_IDENTITY.md §9.2. Agent default is Apex; B/C ship alongside so the
 * owner can swap with a single prop change.
 *
 * A11y: the mark SVG carries the aria-label="NEXIRT"; the wrapping <span>
 * exposes a role="img" + accessible name so screen readers get a single
 * "NEXIRT" annotation regardless of how many SVG children render.
 */
import { type ReactNode } from "react";
import { cn } from "../utils/cn";
import {
  ApexPrimary,
  ApexMonoLight,
  ApexMonoDark,
  ApexAccent,
  ApexOutline,
} from "./marks/apex";
import {
  RefractionPrimary,
  RefractionMonoLight,
  RefractionMonoDark,
  RefractionAccent,
} from "./marks/refraction";
import {
  StackPrimary,
  StackMonoLight,
  StackMonoDark,
  StackAccent,
} from "./marks/stack";
import { Wordmark } from "./wordmark";

export type LogoConcept = "apex" | "refraction" | "stack";
export type LogoVariant = "horizontal" | "stacked" | "wordmark" | "mark-only";
export type LogoColorMode = "primary" | "mono-light" | "mono-dark" | "accent" | "outline";
export type LogoSize = "sm" | "md" | "lg" | "xl";

const markSizeMap: Record<LogoSize, number> = { sm: 24, md: 32, lg: 48, xl: 64 };
const wordSizeMap: Record<LogoSize, number> = { sm: 16, md: 22, lg: 32, xl: 44 };

function pickMark(concept: LogoConcept, colorMode: LogoColorMode): (p: { width?: number; height?: number }) => ReactNode {
  if (concept === "apex") {
    if (colorMode === "primary") return ApexPrimary;
    if (colorMode === "mono-light") return ApexMonoLight;
    if (colorMode === "mono-dark") return ApexMonoDark;
    if (colorMode === "accent") return ApexAccent;
    return ApexOutline;
  }
  if (concept === "refraction") {
    if (colorMode === "primary") return RefractionPrimary;
    if (colorMode === "mono-light") return RefractionMonoLight;
    if (colorMode === "mono-dark") return RefractionMonoDark;
    return RefractionAccent; // refraction has no outline; fall back to accent
  }
  // stack
  if (colorMode === "primary") return StackPrimary;
  if (colorMode === "mono-light") return StackMonoLight;
  if (colorMode === "mono-dark") return StackMonoDark;
  return StackAccent; // stack has no outline; fall back to accent
}

function wordColor(colorMode: LogoColorMode): string {
  if (colorMode === "mono-dark") return "#0A0B10";
  if (colorMode === "accent") return "#6B4FE6";
  // primary, mono-light, outline — all on dark surfaces, wordmark stays light
  return "#F5F5F5";
}

export interface LogoProps {
  concept?: LogoConcept;
  variant?: LogoVariant;
  colorMode?: LogoColorMode;
  size?: LogoSize;
  className?: string;
  ariaLabel?: string;
}

export function Logo({
  concept = "apex",
  variant = "horizontal",
  colorMode = "mono-light",
  size = "md",
  className,
  ariaLabel = "NEXIRT",
}: LogoProps) {
  const Mark = pickMark(concept, colorMode);
  const markPx = markSizeMap[size];
  const wordPx = wordSizeMap[size];
  const wrapLabel = { role: "img", "aria-label": ariaLabel } as const;

  if (variant === "mark-only") {
    return (
      <span className={cn("inline-flex", className)} {...wrapLabel}>
        <Mark width={markPx} height={markPx} />
      </span>
    );
  }
  if (variant === "wordmark") {
    return (
      <span
        className={cn("inline-flex items-center", className)}
        style={{ color: wordColor(colorMode) }}
        {...wrapLabel}
      >
        <Wordmark height={wordPx} />
      </span>
    );
  }
  if (variant === "stacked") {
    return (
      <span
        className={cn("inline-flex flex-col items-center", className)}
        style={{ color: wordColor(colorMode), gap: Math.round(markPx * 0.5) }}
        {...wrapLabel}
      >
        <Mark width={markPx} height={markPx} />
        <Wordmark height={wordPx} />
      </span>
    );
  }
  // horizontal (default)
  return (
    <span
      className={cn("inline-flex items-center", className)}
      style={{ color: wordColor(colorMode), gap: Math.round(markPx * 0.5) }}
      {...wrapLabel}
    >
      <Mark width={markPx} height={markPx} />
      <Wordmark height={wordPx} />
    </span>
  );
}
